/**
 * Cross-store orchestrations that touch multiple stores atomically.
 * These are plain functions (not in any store) — import and call directly.
 */
import { unstable_batchedUpdates } from 'react-dom';
import i18next from 'i18next';
import type { TimelineProject, AudioClip } from '@/types/timeline.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';
import { useProjectStore, retimeTransitionToVideo } from './projectStore.ts';
import { clearPendingTileUpdates } from './pendingTileUpdates.ts';
import { flushSceneReadyBuffer } from './sceneReadyBuffer.ts';
import { markAnimaticRevertPending, clearAnimaticRevertGuard } from './projectStore.ts';
import { emitLoadProjectSwapPerf, emitHistoryAssetsPerf } from '@/perf/loadPerf.ts';
import {
  fingerprintAudioClips, loadedAudioFingerprints, recordLoadedAudio, reconcileAudioClips,
  type AudioReconcilePlan,
} from './audioReconcile.ts';
import { usePlaybackStore } from './playbackStore.ts';
import { useAudioStore } from './audioStore.ts';
import { useCommentStore } from './commentStore.ts';
import { useAiStore } from './aiStore.ts';
import { useUndoStore } from './undoStore.ts';
import { useUiStore } from './uiStore.ts';
import { isReadOnly } from './guards.ts';
import { capabilities } from '@/capabilities.ts';
import { bufferCacheIds, clearAllBuffers, releaseBuffer, revokeBlobUrl, stashBlob } from '@/previs/audioBlobStore.ts';
import { clearPersistedHistoryTracking, getCachedAssetBytes, createAssetUrl } from '@/previs/assetStorage.ts';
import { DEFAULT_NEW_SHOT_FOCAL_LENGTH_MM } from '@/previs/cameraLens.ts';
import { sketchOverlayToAnnotation } from '@/bridge/sketchOverlayConverter.ts';
import { dropShotFullImageForPendingWrite } from '@/bridge/features/fullImage.ts';
import { computeSceneNumbers, computeShotNumbers } from '@/utils/numberingFormatter.ts';
import { resolveClipPlacement } from '@/store/projectDerived.ts';
import { downscaleDataUrl } from '@/ai/imageResize.ts';

// ── loadProject helpers ───────────────────────────────────────────────────────────

/** Revoke blob URLs from the previous project to prevent memory leaks.
 *  `keepAudioBuffers` is set only by the same-project refresh path, which frees exactly the
 *  clips it reconciled as gone or replaced instead of throwing away every decode. */
function revokeOldBlobUrls(opts?: { keepAudioBuffers?: boolean }): void {
  for (const v of useProjectStore.getState().videoShots) {
    URL.revokeObjectURL(v.objectUrl);
  }
  const prevProject = useProjectStore.getState().project;
  if (prevProject) {
    for (const t of prevProject.transitions) {
      if (t.videoObjectUrl) {
        try { URL.revokeObjectURL(t.videoObjectUrl); } catch { /* ok */ }
      }
    }
  }
  for (const entry of useAiStore.getState().generationHistory) {
    if (entry.objectUrl) {
      try { URL.revokeObjectURL(entry.objectUrl); } catch { /* ok */ }
    }
    if (entry.inputObjectUrl) {
      try { URL.revokeObjectURL(entry.inputObjectUrl); } catch { /* ok */ }
    }
  }
  // NOTE: clearAllBlobs() is NOT called here — loadPrevisFile() clears blobs
  // before loading new ones. Calling it here would wipe freshly-stashed blobs.
  if (opts?.keepAudioBuffers !== true) clearAllBuffers();
  // NOTE: Do NOT call clearAssetByteCache() here — loadPrevisFile() already
  // clears it at the top, then populates it with the NEW project's blob URLs.
  // Calling it again here would revoke the new project's URLs before they render.
  clearPersistedHistoryTracking();
}

/** Scan all entity arrays to find the max ID for _nextId seeding. */
function computeMaxEntityId(project: TimelineProject): number {
  const allIds: number[] = [];
  for (const scene of project.scenes) {
    allIds.push(scene.id);
    for (const shot of scene.shots) allIds.push(shot.id);
  }
  for (const t of project.transitions) allIds.push(t.id);
  for (const c of project.audioClips ?? []) allIds.push(c.id);
  for (const c of project.comments ?? []) allIds.push(c.id);
  for (const a of project.approvals ?? []) allIds.push(a.id);
  for (const v of project.videoShots ?? []) allIds.push(v.shotId);
  return allIds.length > 0 ? Math.max(...allIds) : 0;
}

/** Determine progressive loading state, initial active scene, and playback time. */
function computeInitialSceneState(project: TimelineProject) {
  const isProgressive = !!project.readySceneIds;
  const readySceneIds = isProgressive
    ? new Set<number>(project.readySceneIds)
    : new Set<number>(project.scenes.map(s => s.id));
  if (isProgressive && project.scenes.length <= 1 && project.scenes.length > 0) {
    readySceneIds.add(project.scenes[0].id);
  }

  // Always default to "All Scenes" view
  const activeSceneId: number | null = null;
  const currentTime = 0;

  return {
    readySceneIds,
    allScenesReady: !isProgressive || project.scenes.length <= 1,
    activeSceneId,
    currentTime,
  };
}

/** Convert sketch overlays from Unity into annotation comments. */
function mergeSketchOverlayComments(project: TimelineProject) {
  const comments = project.comments ?? [];
  if (!project.sketchOverlays?.length) return comments;

  const IMPORT_TEXT = '[Sketch overlay imported from Previs Pro]';
  const importedShotIds = new Set(
    comments.filter(c => c.text === IMPORT_TEXT).map(c => c.shotId),
  );
  let nextId = comments.reduce((m, c) => Math.max(m, c.id), 0) + 1;
  const newComments: typeof comments = [];

  for (const overlay of project.sketchOverlays) {
    if (importedShotIds.has(overlay.shotId)) continue;
    const annotationData = sketchOverlayToAnnotation(overlay.definition);
    if (!annotationData) continue;
    const svgJson = JSON.stringify(annotationData);
    newComments.push({
      id: nextId++,
      shotId: overlay.shotId,
      timecode: null,
      author: 'Previs Pro',
      text: IMPORT_TEXT,
      createdAt: new Date().toISOString(),
      resolvedAt: null,
      parentCommentId: null,
      annotationSvg: svgJson,
    });
  }
  return newComments.length > 0 ? [...comments, ...newComments] : comments;
}

/** Apply computed scene/shot numbering when documentSettings is present.
 *  Mirrors Unity's SceneAndShotNumberingManager:
 *  - Locked: numbers are frozen in DB, use as-is
 *  - Unlocked: compute on the fly (empty = auto, non-empty = manual override) */
function applyComputedNumbering(project: TimelineProject) {
  if (!project.documentSettings) return project.scenes;
  const ds = project.documentSettings;

  // When locked, Unity writes computed labels to the DB — use them directly
  if (ds.isNumberingLocked) return project.scenes;

  const sceneNumbers = computeSceneNumbers(
    project.scenes.map(s => ({ number: s.numberOverride ?? s.number })),
    ds.startingSceneNumber,
  );
  return project.scenes.map((scene, i) => {
    // Raw override, not the display letter: this runs on every repush, so reading `number` would
    // re-freeze whatever label the previous pass computed and a reordered shot would keep its old one.
    const shotNumbers = computeShotNumbers(
      scene.shots.map(s => ({ number: s.numberOverride ?? s.number })),
      ds.useFullAlphabet, ds.useDigitsForShots,
    );
    return {
      ...scene,
      number: sceneNumbers[i],
      shots: scene.shots.map((shot, j) => ({
        ...shot,
        number: shotNumbers[j],
      })),
    };
  });
}

/** Fetch audio bytes from file:// URLs in embedded mode, then decode all clips. */
async function loadBridgeAudio(clips: AudioClip[]): Promise<void> {
  const clipsWithUrls = clips.filter(c => c.audioFileUrl);

  if (clipsWithUrls.length === 0) {
    // No URL-based clips, but blobs may already be stashed from SQLite load.
    // Trigger decode so embedded-mode audio (loaded via previsLoader) gets AudioBuffers.
    await useAudioStore.getState().processAudioClips();
    return;
  }

  await Promise.all(clipsWithUrls.map(async (clip) => {
    try {
      const resp = await fetch(clip.audioFileUrl!);
      const rawData = new Uint8Array(await resp.arrayBuffer());
      stashBlob(clip.id, rawData);
    } catch (err) {
      console.error(`[loadProject] Failed to fetch audio for clip ${clip.id}:`, err);
      import('@/components/Toast.tsx').then(({ showToast }) =>
        showToast(i18next.t('errors:audioClipFailedToLoad', { clipLabel: clip.label ?? clip.id }), 'error'));
    }
  }));
  // Decode all stashed blobs into AudioBuffers
  await useAudioStore.getState().processAudioClips();
}

/** Fetch generation history assets from file:// URLs → blob URLs in embedded mode. */
function loadBridgeHistoryAssets(historyEntries: GenerationHistoryEntry[]): void {
  if (historyEntries.length === 0) {
    emitHistoryAssetsPerf(0, 0, 0, 0);
    return;
  }

  (async () => {
    const startedAt = performance.now();
    let fileFetches = 0;
    let setStates = 0;
    const { cacheAssetBytes } = await import('@/previs/assetStorage.ts');

    // Convert file:// URLs → blob URLs for output and input assets
    for (const entry of historyEntries) {
      try {
        if (entry.objectUrl && entry.objectUrl.startsWith('file:')) {
          fileFetches += 1;
          const resp = await fetch(entry.objectUrl);
          const bytes = new Uint8Array(await resp.arrayBuffer());
          const blob = new Blob([bytes], { type: entry.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          cacheAssetBytes(blobUrl, bytes);
          entry.objectUrl = blobUrl;
        }
        if (entry.inputObjectUrl && entry.inputObjectUrl.startsWith('file:')) {
          fileFetches += 1;
          const resp = await fetch(entry.inputObjectUrl);
          const bytes = new Uint8Array(await resp.arrayBuffer());
          const blob = new Blob([bytes], { type: 'image/jpeg' });
          const blobUrl = URL.createObjectURL(blob);
          cacheAssetBytes(blobUrl, bytes);
          entry.inputObjectUrl = blobUrl;
        }
      } catch (err) {
        console.warn(`[loadProject] Failed to fetch history asset ${entry.id}:`, err);
      }
    }

    // Resolve input references across entries
    const historyById = new Map(historyEntries.map(e => [e.id, e]));
    for (const entry of historyEntries) {
      if (!entry.inputObjectUrl && entry.inputRefHistoryId) {
        const ref = historyById.get(entry.inputRefHistoryId);
        if (ref?.inputObjectUrl) {
          entry.inputObjectUrl = ref.inputObjectUrl;
        }
      }
    }

    // Build runtime videoShots from history video entries.
    // Only attach videos where the shot's historyId still points to this entry —
    // if the shot was reverted to live in Unity, its historyId is cleared.
    const videoShots: Array<{ shotId: number; objectUrl: string; mimeType: string; historyId: string }> = [];
    {
      const allShots = useProjectStore.getState().project?.scenes.flatMap(s => s.shots) ?? [];
      const shotHistoryMap = new Map(allShots.map(s => [s.id, s.historyId]));
      for (const entry of historyEntries) {
        if (entry.assetType === 'video' && entry.shotId != null && entry.objectUrl) {
          if (shotHistoryMap.get(entry.shotId) === entry.id) {
            videoShots.push({
              shotId: entry.shotId,
              objectUrl: entry.objectUrl,
              mimeType: entry.mimeType,
              historyId: entry.id,
            });
          }
        }
      }
    }

    // Everything below writes shot tile fields, and the awaits above span the window in which the
    // reload's sceneReady burst arrives — drain it here so the history-derived URLs win over a push
    // that was buffered earlier. See sceneReadyBuffer.ts (ORDERING, outbound).
    flushSceneReadyBuffer();

    // Update AI store with fetched entries
    setStates += 1;
    useAiStore.setState({ generationHistory: [...historyEntries] });

    // Apply video URLs to shots
    if (videoShots.length > 0) {
      const currentProject = useProjectStore.getState().project;
      if (currentProject) {
        const videoMap = new Map(videoShots.map(v => [v.shotId, v]));
        const updatedScenes = currentProject.scenes.map(scene => ({
          ...scene,
          shots: scene.shots.map(shot => {
            const v = videoMap.get(shot.id);
            return v ? { ...shot, videoObjectUrl: v.objectUrl } : shot;
          }),
        }));
        setStates += 1;
        useProjectStore.setState({
          project: { ...currentProject, scenes: updatedScenes },
          videoShots,
        });
      }
    }

    // 20260825 - History-linked shot thumbnails are NOT applied here any more. This function has
    // exactly one caller (loadProject, inside its `capabilities.bridge` branch), so it only ever
    // ran embedded — where Unity owns every board tile and the history image is the RAW, UNCROPPED
    // model output. It was the async twin of previsLoader's graft (now suppressed embedded via
    // LoadPrevisFileOptions.suppressHistoryTileGraft): because it re-asserted AFTER the awaits
    // above, and after deliberately draining the sceneReady buffer, it could repaint the uncropped
    // image on top of a correct Unity push that had already landed — the second, later flash.
    // The buffer drain above stays: the video/transition writes below still touch shot tile fields.

    // Apply transition video URLs from history
    {
      const currentProject = useProjectStore.getState().project;
      if (currentProject) {
        let changed = false;
        const updatedTransitions = currentProject.transitions.map(t => {
          if (t.videoHistoryId) {
            const histEntry = historyById.get(t.videoHistoryId);
            if (histEntry?.objectUrl) {
              changed = true;
              return { ...t, videoObjectUrl: histEntry.objectUrl, videoMimeType: histEntry.mimeType };
            }
          }
          return t;
        });
        if (changed) {
          setStates += 1;
          useProjectStore.setState({
            project: { ...currentProject, transitions: updatedTransitions },
          });
        }
      }
    }

    // Mark all entries as persisted (they came from Unity's SQLite)
    const { markHistoryPersistedBatch } = await import('@/previs/assetStorage.ts');
    markHistoryPersistedBatch(historyEntries.map(e => e.id));
    emitHistoryAssetsPerf(historyEntries.length, fileFetches, setStates, performance.now() - startedAt);
  })();
}

/** Initialize all bridge sync modules for embedded mode. */
function initBridgeSyncs(project: TimelineProject, clips: AudioClip[]): void {
  import('@/bridge/audioSyncBridge.ts').then(m => {
    m.initAudioSync(clips.map(c => c.id));
  });
  import('@/bridge/transitionSyncBridge.ts').then(m => {
    m.initTransitionSync();
  });
  import('@/bridge/generationHistorySyncBridge.ts').then(m => {
    m.initGenerationHistorySync(project.knownBlobHistoryIds ?? []);
  });
  import('@/bridge/commentSyncBridge.ts').then(m => {
    m.initCommentSync();
  });
  import('@/bridge/aiSettingsSyncBridge.ts').then(m => {
    m.initAiSettingsSync();
  });
  import('@/bridge/coverageSyncBridge.ts').then(m => {
    m.initCoverageSync();
  });
}

/**
 * Load a project — resets ALL stores atomically, then awaits audio decode.
 *
 * `preserveUiState` (default false, only set by the embedded live-board
 * refresh path — see embeddedBridge's `setSqlitePath` `refresh` flag) keeps
 * the expanded/active/selected UI state across the reload when the
 * referenced scene/shot still exists in the incoming project. Undo stacks
 * still clear regardless — reloading data mid-undo-history is unsafe.
 * It also lets the load reuse already-decoded audio instead of re-decoding
 * the whole project's clips before it resolves (see audioReconcile.ts).
 */
export async function loadProject(incoming: TimelineProject, opts?: { preserveUiState?: boolean }): Promise<void> {
  // A tile push still sitting in the sceneReady buffer describes the DB generation this load is
  // about to replace. Apply it rather than drop it: entries naming rows the outgoing project
  // already holds land where they belong, and entries naming rows it doesn't — the targeted
  // add-shot/add-scene push — fall through to the pending-tile stash the reload's carry-over
  // grafts from, which is the whole point of that stash. This must precede the clear below or a
  // project switch would leave the stash holding the PREVIOUS project's URLs against colliding
  // ids. In embedded practice the buffer is already empty here: dispatchMessage drained it when
  // setSqlitePath arrived, which is also what keeps the carry-over (upstream of this call, in
  // sqliteBridge) able to see the stash at all.
  flushSceneReadyBuffer();
  // The animatic-revert guard (projectStore.markAnimaticRevertPending) ends here: the revert's own
  // repush is what brings us through this load, after Unity has forgotten the mp4 — so the tile
  // pushes that follow are honest again. After the flush above, so a buffered pre-repush push
  // was still answered by the guard.
  clearAnimaticRevertGuard();
  // A fresh open / project switch invalidates every tile push still waiting for its row:
  // shot ids collide across projects, so a survivor would render a confidently wrong image.
  if (opts?.preserveUiState !== true) clearPendingTileUpdates();

  // A same-project refresh reuses the audio it already decoded (see audioReconcile). Both halves
  // of the gate matter: preserveUiState is the same-project signal Unity's repush sets, and the
  // title guards the case where it ever arrives across a project switch — clip ids collide there,
  // and a kept buffer would be another project's audio. Anything else takes the full path below.
  //
  // The title is a weak identifier (two untitled projects match), so it leans on what the native
  // side guarantees today: refresh:true has exactly one sender, RepushProjectToLiveWebView, which
  // always names the currently open document; and switching projects tears the WebView down, so a
  // new page starts with an empty store AND an empty snapshot and fails this gate on prevProject.
  // If the board WebView ever gains project-switch-without-teardown (a keep-alive analogue of
  // cs:5441), this gate must be revisited — it would need the sqlite path or a documentversion
  // token, not the title.
  const prevProject = useProjectStore.getState().project;
  const audioPlan: AudioReconcilePlan | null =
    opts?.preserveUiState === true && prevProject != null && prevProject.title === incoming.title
      ? reconcileAudioClips(loadedAudioFingerprints(), fingerprintAudioClips(incoming.audioClips ?? []))
      : null;

  revokeOldBlobUrls({ keepAudioBuffers: audioPlan != null });

  if (audioPlan) {
    // What the blanket clearAllBuffers above would have freed for these clips, plus the clip's
    // tracked blob URL — normally already revoked by the loader's clearAllBlobs, but a released
    // clip is leaving either way, so this owns it rather than assuming that call ran.
    for (const clipId of audioPlan.toRelease) {
      releaseBuffer(clipId);
      revokeBlobUrl(clipId);
    }
    // Then anything cached that the incoming project doesn't account for at all: a clip created
    // and removed in-session never reaches a repush's rows, so it appears in neither side of the
    // reconcile, and removeAudioClip deliberately keeps its buffer for undo. clearAllBuffers used
    // to collect those as a side effect — decoded PCM dwarfs the compressed bytes and this
    // platform has a jetsam-OOM history, so the reconciled path sweeps them explicitly. Undo isn't
    // a reason to keep them: the load clears the undo stacks below.
    const accountedFor = new Set([...audioPlan.toKeep, ...audioPlan.toLoad]);
    for (const clipId of bufferCacheIds()) {
      if (accountedFor.has(clipId)) continue;
      releaseBuffer(clipId);
      revokeBlobUrl(clipId);
    }
    if (capabilities.bridge) {
      import('@/bridge/embeddedBridge.ts').then(m => m.enqueueSendToNative('diagLog', {
        msg: `loadProject: audio reuse — ${audioPlan.toKeep.length} kept, ${audioPlan.toLoad.length} to load, ${audioPlan.toRelease.length} released`,
      }));
    }
  }

  const maxId = computeMaxEntityId(incoming);
  const sceneState = computeInitialSceneState(incoming);
  const mergedComments = mergeSketchOverlayComments(incoming);
  const numberedScenes = applyComputedNumbering(incoming);

  // Build final project with sketch overlays and numbering applied
  const project = (mergedComments !== (incoming.comments ?? []) || numberedScenes !== incoming.scenes)
    ? { ...incoming, comments: mergedComments, scenes: numberedScenes }
    : incoming;

  const preserveUiState = opts?.preserveUiState === true;
  const prevExpandedSceneId = preserveUiState ? useUiStore.getState().expandedSceneId : null;
  const prevActiveSceneId = preserveUiState ? useProjectStore.getState().activeSceneId : null;
  const prevSelectedShotId = preserveUiState ? useProjectStore.getState().selectedShotId : null;
  // Captured alongside the selection it belongs to: userInitiatedShotSelection is what gates the
  // board's shot actions panel open, so a same-project repush (canvas return, thumbnail refresh)
  // must carry it across EXACTLY when it carries selectedShotId across. Zustand's setState merges,
  // so omitting the key would already leave the flag alone — writing it explicitly makes that a
  // stated invariant rather than an accident of merge semantics, and guarantees the pair can never
  // drift apart (flag true with the selection dropped = a panel gate pointing at nothing).
  const prevUserInitiatedShotSelection = preserveUiState
    ? useProjectStore.getState().userInitiatedShotSelection
    : false;
  const preservedSceneIds = preserveUiState ? new Set(project.scenes.map((s) => s.id)) : null;
  const preservedShotIds = preserveUiState ? new Set(project.scenes.flatMap((s) => s.shots.map((sh) => sh.id))) : null;

  const swapStartedAt = performance.now();
  unstable_batchedUpdates(() => {
    useProjectStore.setState((prev) => ({
      project,
      // THE committed-load counter (projectStore's declaration). Bumped here and nowhere else, so
      // `projectLoadSeq > captured` is proof a repushed slim DB actually MERGED — a test a bare
      // `project !== captured` fails, because markScenesReady replaces the object on every tile batch.
      // Functional updater so two loads landing in the same tick cannot read the same base and collide.
      projectLoadSeq: prev.projectLoadSeq + 1,
      selectedShotId: preserveUiState && prevSelectedShotId != null && preservedShotIds!.has(prevSelectedShotId)
        ? prevSelectedShotId
        : null,
      // Same predicate as selectedShotId above, by design — the flag rides with the selection or
      // is dropped with it. A dropped/replaced selection is never "user initiated".
      userInitiatedShotSelection:
        preserveUiState && prevSelectedShotId != null && preservedShotIds!.has(prevSelectedShotId)
          ? prevUserInitiatedShotSelection
          : false,
      selectedAudioClipId: null,
      activeSceneId: preserveUiState && (prevActiveSceneId === null || preservedSceneIds!.has(prevActiveSceneId))
        ? prevActiveSceneId
        : sceneState.activeSceneId,
      videoShots: project.videoShots ?? [],
      readySceneIds: sceneState.readySceneIds,
      allScenesReady: sceneState.allScenesReady,
      // Every push, refresh included, is answered by a fresh Unity burst that ends in allScenesReady;
      // until that lands the fill presentation for THIS load is not final (projectStore's comment).
      loadBurstSettled: false,
      // A fresh open / project switch has no fill yet — Unity gates boardFillState snapshots on the
      // web's projectLoaded ack, which follows this load. So a stale incomplete fill from the
      // PREVIOUS document would shimmer whichever scenes of the new one happen to share its pending
      // ids, and its dismissal bit would make the new project's first snapshot read as "the same
      // fill" (the reset in setBoardFillState only fires when the previous fill was absent or
      // complete), demoting a modal-sized fill to the pill. A preserving repush is the same
      // document mid-fill and keeps both.
      ...(preserveUiState ? {} : { boardFill: null, boardFillModalDismissed: false }),
      reviewSessionId: project.reviewSessionId ?? null,
      customScriptTexts: {},
      editedScreenplaySceneIds: new Set<number>(),
      _nextId: maxId + 1,
    }));

    usePlaybackStore.setState({
      currentTime: sceneState.currentTime,
      isPlaying: false,
    });

    useAudioStore.setState({
      audioClips: project.audioClips ?? [],
    });

    useCommentStore.setState({
      comments: project.comments ?? [],
      approvals: project.approvals ?? [],
    });

    useAiStore.setState({
      aiAudioHints: project.aiAudioHints ?? {},
      generationHistory: project.generationHistory ?? [],
      voiceAssignments: project.voiceAssignments ?? [],
      activeEnhancementPreview: null,
    });

    useUndoStore.getState().clearStacks();
    // The Script-tab browse selection (uiStore's screenplay slice) follows the exact lifecycle of
    // expandedSceneId below: cleared on a fresh open / project switch, kept across a preserving
    // same-project repush with the two SCENE-id fields validated against the incoming scene set.
    // The name/key-keyed fields (character, location) and the search query cannot be id-validated
    // and simply ride across a preserving repush — a stale key matches no card, which is harmless.
    // Read via getState() here in the batch: nothing above writes uiStore, so these are still the
    // pre-swap values (the same guarantee the prevExpandedSceneId capture above provides).
    const uiPrev = useUiStore.getState();
    useUiStore.setState({
      expandedSceneId:
        preserveUiState && prevExpandedSceneId != null && preservedSceneIds!.has(prevExpandedSceneId)
          ? prevExpandedSceneId
          : null,
      screenplayExpandedSceneId:
        preserveUiState && uiPrev.screenplayExpandedSceneId != null && preservedSceneIds!.has(uiPrev.screenplayExpandedSceneId)
          ? uiPrev.screenplayExpandedSceneId
          : null,
      screenplayFilterSceneId:
        preserveUiState && uiPrev.screenplayFilterSceneId != null && preservedSceneIds!.has(uiPrev.screenplayFilterSceneId)
          ? uiPrev.screenplayFilterSceneId
          : null,
      ...(preserveUiState ? {} : {
        screenplayExpandedCharacter: null,
        // 20260921 — the one-shot cast-card reveal latch follows the expanded CHARACTER exactly:
        // dropped on a fresh open / project switch, and deliberately KEPT across a preserving
        // repush, because the preserving repush is precisely what carries the newly baked cast row
        // the latch is waiting for (uiStore's screenplayRevealCharacter).
        screenplayRevealCharacter: null,
        screenplayExpandedLocation: null,
        screenplaySearchQuery: '',
        // 20260909 — the board's SELECT MODE follows expandedSceneId's lifecycle exactly: a fresh
        // open / project switch drops it, a preserving same-project repush keeps it (the board
        // prunes the ids against the incoming shot set itself, which is the id-validation this
        // block does for the scene fields above).
        boardSelectMode: false,
        boardSelectedShotIds: [],
        // 20260920 — the script panel's "the user has decided" flag joins them on the same terms,
        // and the PRESERVING half matters more than the fresh-open half: Unity repushes the same
        // project constantly, and re-deciding the panel on each repush would be a louder version of
        // the bug that flag exists to kill (see scriptPanelDecidedByUser in uiStore.ts). A
        // different project, on the other hand, is a fresh start and gets the shipped auto-open
        // back — the choice is remembered for the project the user made it in, not forever.
        scriptPanelDecidedByUser: false,
      }),
    });
  });
  emitLoadProjectSwapPerf(
    performance.now() - swapStartedAt,
    project.scenes.length,
    project.scenes.reduce((sum, s) => sum + s.shots.length, 0),
  );

  // Await audio decode — loading screen stays visible until this resolves
  try {
    if (capabilities.bridge) {
      const clips = project.audioClips ?? [];
      // On a reconciled reload only the changed clips are fetched. The kept ones still go through
      // processAudioClips (loadBridgeAudio's tail), which re-attaches their cached buffers to the
      // incoming rows without decoding — so the gate stays "load complete implies audio ready".
      const toLoadIds = audioPlan ? new Set(audioPlan.toLoad) : null;
      await loadBridgeAudio(toLoadIds ? clips.filter(c => toLoadIds.has(c.id)) : clips);
      // These remain fire-and-forget (not part of the loading gate)
      loadBridgeHistoryAssets(project.generationHistory ?? []);
      initBridgeSyncs(project, clips);
    } else {
      // Standalone/review: blobs already stashed by previsLoader, just decode
      await useAudioStore.getState().processAudioClips();
    }
  } finally {
    // The old side of the next refresh's reconcile. Taken after the load so URL-sourced clips are
    // fingerprinted against the bytes their fetch just stashed — and in a finally so it can never
    // describe a different project than the store holds: the store was set before the await, so a
    // throw here would otherwise leave the previous project's fingerprints as the next old side.
    recordLoadedAudio(fingerprintAudioClips(project.audioClips ?? []));
  }
}

/**
 * Save project — reads all stores, writes to .previs SQLite.
 */
export async function saveProject(): Promise<void> {
  const ps = useProjectStore.getState();
  if (isReadOnly()) return;
  // Embedded persists through Unity (native File menu / bridge). The browser download-export is a
  // standalone-only affordance and, with the slim embedded DB, would emit a truncated .previs — so
  // block it in embedded. (capabilities.fileIO === !isEmbedded.)
  if (!capabilities.fileIO) return;
  const { originalBytes, fileName, project, isSaving } = ps;
  const { audioClips } = useAudioStore.getState();
  const { comments, approvals } = useCommentStore.getState();
  const { generationHistory, aiAudioHints, voiceAssignments } = useAiStore.getState();
  const reviewSessionId = ps.reviewSessionId;

  if (!originalBytes || !project || isSaving) return;

  useProjectStore.setState({ isSaving: true, saveError: null });
  try {
    const { savePrevisFile } = await import('@/previs/previsSaver.ts');
    const savedBytes = await savePrevisFile({
      originalBytes,
      project,
      audioClips,
      comments,
      approvals,
      generationHistory,
      aiAudioHints,
      voiceAssignments,
      reviewSessionId,
    });

    const baseName = fileName.replace(/\.previs$/i, '');
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const defaultName = `${baseName}_${ts}.previs`;
    const chosenName = window.prompt(i18next.t('errors:savePromptDefault'), defaultName);
    if (!chosenName) return;
    const finalName = chosenName.endsWith('.previs') ? chosenName : `${chosenName}.previs`;

    const blob = new Blob([savedBytes.buffer as ArrayBuffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    useProjectStore.setState({ originalBytes: new Uint8Array(savedBytes), fileName: finalName });

    // Entries are now persisted to SQLite — prune old blob URLs
    useAiStore.getState().pruneOldBlobUrls();

    // Blobs persist in memory — no eviction. This ensures save → reload → save
    // still has audio data, and split/export/publish always find raw bytes.

    const { showToast } = await import('@/components/Toast.tsx');
    showToast(i18next.t('errors:savedFile', { fileName: finalName }));
  } catch (err) {
    console.error('Failed to save .previs file:', err);
    const msg = err instanceof Error ? err.message : 'Unknown error';
    useProjectStore.setState({ saveError: msg });
    const { showToast } = await import('@/components/Toast.tsx');
    showToast(i18next.t('errors:saveFailed', { message: msg }), 'error');
  } finally {
    useProjectStore.setState({ isSaving: false });
  }
}

/**
 * Accept an AI enhancement — sets shot.historyId + shot.thumbnailDataUrl.
 */
export function acceptEnhancement(shotId: number): void {
  if (isReadOnly()) return;
  // A sceneReady buffered up to 50 ms ago could otherwise land on top of this write and replace the
  // accepted image with Unity's 3D tile. See sceneReadyBuffer.ts (ORDERING, outbound) — and note the
  // snapshot below is taken AFTER the flush on purpose: reading it first would build this write from
  // a project that predates the batch, reverting every OTHER shot the flush just applied.
  flushSceneReadyBuffer();
  const ps = useProjectStore.getState();
  useUndoStore.getState().pushSnapshot();

  const ai = useAiStore.getState();
  const { activeEnhancementPreview, generationHistory } = ai;
  const { project, videoShots } = ps;
  if (!activeEnhancementPreview || activeEnhancementPreview.shotId !== shotId || !project) return;

  const historyEntry = generationHistory.find(h => h.id === activeEnhancementPreview.historyId);
  const enhancedUrl = historyEntry?.objectUrl ?? '';

  // Same reciprocity contract as applyBatchGenerations — an applied image replaces an applied video.
  const updatedScenes = project.scenes.map(scene => ({
    ...scene,
    shots: scene.shots.map(shot =>
      shot.id === shotId
        ? { ...shot, thumbnailDataUrl: enhancedUrl, historyId: activeEnhancementPreview.historyId, hasStaticImage: true, videoObjectUrl: undefined }
        : shot
    ),
  }));

  // Note 4 from the WEB side (bridge/features/fullImage.ts). Every optimistic image write in this
  // file has to evict, and it has to happen HERE rather than in the hook: the shot id and
  // hasStaticImage are both unchanged by this write, so useShotFullImage's deps never fire — and
  // even if they did, the re-subscribe would be served the stale entry straight out of the cache.
  // Left unevicted, the previous _full URL keeps WINNING over the image just accepted
  // (Viewer's `inputUrl ?? fullResUrl ?? thumbnailDataUrl`) until Unity's poke drains, and if Unity
  // deleted that file in the meantime the viewer goes blank rather than merely stale. Kept in the
  // same synchronous block as the store write so React commits the reset and the new thumbnail
  // together — one render, no flash.
  //
  // The …ForPendingWrite variant is the one that evicts WITHOUT re-asking, and that is not a detail:
  // sendStaticImageToUnity below cannot reach the outbox until a microtask at the earliest (dynamic
  // import, plus fetch+FileReader for a blob: URL), while a re-ask would land in it synchronously.
  // FIFO means Unity would answer it FIRST, from the blob we are in the middle of replacing, and the
  // viewer would flip back to the previous picture for the whole stale window. Unity's poke, once
  // the new blob is in, is what re-asks.
  dropShotFullImageForPendingWrite(shotId);

  unstable_batchedUpdates(() => {
    useAiStore.setState({
      activeEnhancementPreview: null,
    });
    useProjectStore.setState({
      videoShots: videoShots.filter(v => v.shotId !== shotId),
      project: { ...project, scenes: updatedScenes },
    });
  });

  // Ensure the viewer shows the enhanced image, not the "before" input
  if (useAiStore.getState().showInputImage) {
    useAiStore.getState().toggleShowInputImage();
  }

  // Persist to Unity as a static shot image + update shot historyId FK
  if (capabilities.bridge) {
    import('@/bridge/embeddedBridge.ts').then(({ sendStaticImageToUnity }) => {
      sendStaticImageToUnity(shotId, enhancedUrl);
    });
    import('@/bridge/generationHistorySyncBridge.ts').then(({ sendShotHistoryIdToUnity }) => {
      sendShotHistoryIdToUnity(shotId, activeEnhancementPreview.historyId, 'image');
    });
  }
}

/**
 * Revert a shot to its live 3D view — clears AI enhancement, imported static
 * image, and/or video.  In embedded mode, tells Unity to clear the static
 * image + history_id + video in the DB and refresh the canvas editor.
 * The 3D cache thumbnail will arrive via the next sceneReady update.
 *
 * Returns a promise that settles once the bridge verbs are ENQUEUED (20260925, the board's
 * Replace → Build in 3D door). The sends go through a dynamic import, so a caller that sends its
 * own verb synchronously right after this call (enterCanvasForShot) would put it on the queue
 * AHEAD of revertShotToLive; awaiting this keeps Unity's order the call order. Resolves at once on
 * every early-out and outside the bridge. Fire-and-forget callers can keep ignoring it.
 */
export function revertShotToLive(shotId: number): Promise<void> {
  if (isReadOnly()) return Promise.resolve();
  // Drain first, or a buffered push restores the thumbnail/video this call is clearing — then read
  // state AFTER the flush, or this write reverts the rest of the batch.
  flushSceneReadyBuffer();
  const ps = useProjectStore.getState();
  useUndoStore.getState().pushSnapshot();

  const { project, videoShots } = ps;
  if (!project) return Promise.resolve();

  // Find the shot to check if it's an animatic
  const shot = project.scenes.flatMap(s => s.shots).find(s => s.id === shotId);
  const wasAnimatic = shot?.isAnimatic;

  // Clear thumbnail, historyId, videoObjectUrl, and isAnimatic flag
  const updatedScenes = project.scenes.map(scene => ({
    ...scene,
    shots: scene.shots.map(s =>
      s.id === shotId
        ? { ...s, thumbnailDataUrl: undefined, historyId: undefined, hasStaticImage: false, videoObjectUrl: undefined, isAnimatic: undefined }
        : s
    ),
  }));

  // Evict the on-demand full-res entry (see acceptEnhancement above). Unity DELETES {shotId}_full
  // on this path, so a kept entry is a dangling file:// URL — and hasStaticImage flipping back to
  // true on a re-add would serve it synchronously from cache into a blank viewer. Not re-asking
  // matters twice as much here: a re-ask would be answered from the blob Unity has not deleted yet,
  // re-creating the very dangling entry this call exists to remove.
  dropShotFullImageForPendingWrite(shotId);

  useProjectStore.setState({
    videoShots: videoShots.filter(v => v.shotId !== shotId),
    project: { ...project, scenes: updatedScenes },
  });

  if (capabilities.bridge) {
    return import('@/bridge/embeddedBridge.ts').then(({ enqueueSendToNative }) => {
      // Tell Unity to clear static image + history_id + video and send back the 3D thumbnail
      enqueueSendToNative('revertShotToLive', { shotId });
      // If this was an animatic, also tell Unity to revert the animatic sequence
      // (removes continuing shots, changes category to Normal, deletes cached mp4)
      if (wasAnimatic) {
        enqueueSendToNative('revertAnimaticToSingleShot', { shotId });
      }
    });
  }
  return Promise.resolve();
}

/** @deprecated Use revertShotToLive instead */
export const revertEnhancement = revertShotToLive;

/**
 * Turn an animatic back into a single static shot as the FIRST half of a confirmed Enhance run —
 * owner batch 5, T3 (docs/superpowers/specs/2026-09-03-owner-batch-5-design.md §T3).
 *
 * Why this exists next to revertShotToLive rather than reusing it: Enhance is about to generate a
 * new image FROM this shot's 3D poster, so the poster must survive. revertShotToLive clears
 * `thumbnailDataUrl`/`hasStaticImage` and tells Unity to delete the static image and the
 * `{shotId}_full` file, which would pull the input image out from under the generation that is
 * seconds away. So this clears the ANIMATIC half only:
 *
 *   - `isAnimatic` → undefined, so ShotTypeBadge and every `shot.isAnimatic` branch stop calling it
 *     an animatic in the frame the user confirms.
 *   - `videoObjectUrl` → undefined. This is THE field the bug is about: Unity delivers the animatic
 *     mp4 as a `file://` URL on `sceneReady` (TimelineWebViewPanel's `animaticVideoFilePaths`), the
 *     store lands it here, and the board's selected/hovered tile (`<ShotCardVideo>`), the shot panel
 *     (`videoSrc`) and the shot-info preview all read it. `acceptEnhancement` clears it too, which
 *     is exactly why the old behaviour was INCONSISTENT rather than simply wrong: the video came
 *     straight back on Unity's next sceneReady, because the row was still category Initial with a
 *     cached mp4 on disk. Clearing it web-side is the optimistic half; the bridge verb below is what
 *     makes it stick.
 *   - the `videoShots` row, and `videoHash`. The array and the shot field are two halves of one fact
 *     (sqliteBridge's refresh merge filters one against the other) and a kept hash would read as a
 *     stale asset to the freshness sweep and re-request the deleted mp4.
 *
 * `thumbnailDataUrl`, `hasStaticImage` and `historyId` are deliberately UNTOUCHED — Enhance's own
 * apply path (acceptEnhancement / applyBatchGenerations) overwrites them moments later.
 *
 * ONE bridge verb, and it is not revertShotToLive: Unity's HandleRevertAnimaticToSingleShot removes
 * the continuing shots, sets the category back to Normal, deletes the cached `{shotId}.mp4` and its
 * hash sidecar, drops the shot from `animaticVideoFilePaths` so no later sceneReady re-sends it, and
 * repushes with `refresh:true` (which preserves web UI state).
 *
 * Returns a promise that settles once the verb is ENQUEUED — the same contract, for the same
 * reason, as revertShotToLive's (the board's Replace → Import door awaits it before its own
 * setStaticImage). Resolves at once on every early-out and outside the bridge.
 */
export function revertAnimaticForEnhance(shotId: number): Promise<void> {
  if (isReadOnly()) return Promise.resolve();
  // Drain first, or a push buffered up to 50 ms ago restores the very videoObjectUrl this call is
  // clearing — then read state AFTER the flush, or this write reverts the rest of the batch.
  // See sceneReadyBuffer.ts (ORDERING, outbound).
  flushSceneReadyBuffer();
  const ps = useProjectStore.getState();
  if (capabilities.bridge) {
    // Same policy as projectStore.reorderShotInScene (cs:5467): in embedded mode this mutation is
    // committed natively and irreversibly — Unity deletes the mp4 and its hash sidecar and drops the
    // continuing-shot rows — so a snapshot would let undo restore isAnimatic plus a file:// URL to a
    // deleted file. Drop the history instead of offering a lie (20260903, reviewer). The repush
    // Unity sends back would have wiped the stack anyway.
    useUndoStore.getState().clearStacks();
  } else {
    // Standalone: web-owned and genuinely reversible, so undo stays honest here.
    useUndoStore.getState().pushSnapshot();
  }

  const { project, videoShots } = ps;
  if (!project) return Promise.resolve();

  // Before the write: a sceneReady already buffered for this scene is applied by the flush above,
  // but one arriving in the frame(s) before Unity processes the verb still carries the animatic
  // mp4 — the guard makes applyTileUpdate ignore it until the revert's repush lands (see
  // projectStore.animaticRevertPendingIds).
  markAnimaticRevertPending(shotId);

  const updatedScenes = project.scenes.map(scene => ({
    ...scene,
    shots: scene.shots.map(s =>
      s.id === shotId
        ? { ...s, isAnimatic: undefined, videoObjectUrl: undefined, videoHash: undefined }
        : s
    ),
  }));

  useProjectStore.setState({
    videoShots: videoShots.filter(v => v.shotId !== shotId),
    project: { ...project, scenes: updatedScenes },
  });

  if (capabilities.bridge) {
    return import('@/bridge/embeddedBridge.ts').then(({ enqueueSendToNative }) => {
      enqueueSendToNative('revertAnimaticToSingleShot', { shotId });
    });
  }
  return Promise.resolve();
}

/**
 * Apply batch AI generations — sets thumbnailDataUrl + historyId on each shot.
 */
export function applyBatchGenerations(results: Array<{ shotId: number; enhancedDataUrl: string; historyId: string }>): void {
  if (isReadOnly()) return;
  // Drain first — same inversion as acceptEnhancement, across every shot in the batch — then read
  // state AFTER the flush, or this write reverts the rest of the batch.
  flushSceneReadyBuffer();
  const ps = useProjectStore.getState();
  useUndoStore.getState().pushSnapshot();

  const { project, videoShots } = ps;
  if (!project) return;

  const resultMap = new Map(results.map(r => [r.shotId, r]));

  // Reciprocity with attachVideo: an applied video replaces the shot's image (projectStore.attachVideo
  // sends assetType 'video', Unity clears the static image and re-posters from frame 0), so an applied
  // image has to replace the video the same way. Left attached, the video outranks the image just
  // applied in every display precedence chain — ShotTypeBadge, Viewer, useShotFullImage — so the shot
  // shows one medium and plays the other. 20260825 - now uniform across every image-apply door: the
  // drag-drop import path (attachDroppedImage) and the native canvas/Director-View imports
  // (TimelineWebViewPanel.ClearAppliedImageGenerationForReplacedShotImage) clear the video too.
  const updatedScenes = project.scenes.map(scene => ({
    ...scene,
    shots: scene.shots.map(shot => {
      const r = resultMap.get(shot.id);
      if (r) return { ...shot, thumbnailDataUrl: r.enhancedDataUrl, historyId: r.historyId, hasStaticImage: true, videoObjectUrl: undefined };
      return shot;
    }),
  }));

  // Same eviction as acceptEnhancement above, once per shot in the batch — this is also the path
  // reapplyHistoryEntry's image branch takes.
  for (const r of results) dropShotFullImageForPendingWrite(r.shotId);

  useProjectStore.setState({
    videoShots: videoShots.filter(v => !resultMap.has(v.shotId)),
    project: { ...project, scenes: updatedScenes },
  });

  // Ensure the viewer shows the enhanced image, not the "before" input
  if (useAiStore.getState().showInputImage) {
    useAiStore.getState().toggleShowInputImage();
  }

  // Persist AI-generated images to Unity as static shot images + update historyId FKs
  if (capabilities.bridge) {
    import('@/bridge/embeddedBridge.ts').then(async ({ sendStaticImageToUnity }) => {
      for (const r of results) {
        await sendStaticImageToUnity(r.shotId, r.enhancedDataUrl);
      }
    });
    import('@/bridge/generationHistorySyncBridge.ts').then(({ sendShotHistoryIdToUnity }) => {
      for (const r of results) {
        sendShotHistoryIdToUnity(r.shotId, r.historyId, 'image');
      }
    });
  }
}

/**
 * Reapply a generation history entry.
 */
export function reapplyHistoryEntry(id: string, shotId: number): void {
  const ps = useProjectStore.getState();
  if (isReadOnly()) return;
  useUndoStore.getState().pushSnapshot();

  const ai = useAiStore.getState();
  const entry = ai.generationHistory.find(h => h.id === id);
  if (!entry) return;

  // Guard: pruned entries have no blob URL data
  if (!entry.objectUrl) {
    import('@/components/Toast.tsx').then(({ showToast }) =>
      showToast(i18next.t('errors:assetPrunedFromMemory'), 'error'));
    return;
  }

  if (entry.assetType === 'tts' || entry.assetType === 'audio') {
    // Add TTS/audio clip to audio lane at current playhead time
    reapplyAudioHistoryEntry(entry);
  } else if (entry.assetType === 'transition_video') {
    const { project } = ps;
    if (!project) return;
    let nextShotId: number | null = null;
    for (const scene of project.scenes) {
      const idx = scene.shots.findIndex(s => s.id === shotId);
      if (idx >= 0 && idx < scene.shots.length - 1) {
        nextShotId = scene.shots[idx + 1].id;
        break;
      }
    }
    if (!nextShotId) {
      import('@/components/Toast.tsx').then(({ showToast }) =>
        showToast(i18next.t('errors:noNextShotForTransition'), 'error'));
      return;
    }
    let videoUrl = entry.objectUrl;
    const cachedVideo = getCachedAssetBytes(videoUrl);
    if (cachedVideo) {
      const fresh = createAssetUrl(cachedVideo, entry.mimeType);
      videoUrl = fresh;
      ai.updateHistoryEntryUrl(id, fresh);
    }
    const existing = ps.getTransition(shotId, nextShotId);
    if (!existing || existing.type !== 'continuous') {
      ps.setTransition(shotId, nextShotId, 'continuous');
    }
    ps.attachTransitionVideo(shotId, nextShotId, videoUrl, entry.mimeType, id);
  } else if (entry.assetType === 'video') {
    let videoUrl = entry.objectUrl;
    // Reconstruct blob URL if the original was revoked (e.g., by removeVideo)
    const cachedVideo = getCachedAssetBytes(videoUrl);
    if (cachedVideo) {
      const fresh = createAssetUrl(cachedVideo, entry.mimeType);
      videoUrl = fresh;
      ai.updateHistoryEntryUrl(id, fresh);
    }
    ps.attachVideo(shotId, videoUrl, entry.mimeType, id);
  } else if (entry.assetType === 'image') {
    // When applying to a different shot, create a derived history entry so
    // E-key (showInputImage) shows the correct "before" for this shot.
    let effectiveId = id;
    if (entry.shotId !== shotId) {
      const { project } = ps;
      let targetThumb: string | null = null;
      if (project) {
        for (const scene of project.scenes) {
          const s = scene.shots.find(sh => sh.id === shotId);
          if (s) { targetThumb = s.thumbnailDataUrl ?? null; break; }
        }
      }
      const derived: GenerationHistoryEntry = {
        ...entry,
        id: crypto.randomUUID(),
        shotId,
        inputObjectUrl: targetThumb,
        inputRefHistoryId: null,
        costCredits: 0,
        createdAt: new Date().toISOString(),
      };
      ai.addHistoryEntry(derived);
      effectiveId = derived.id;
    }
    let imageUrl = entry.objectUrl;
    const cachedImage = getCachedAssetBytes(imageUrl);
    if (cachedImage) {
      const fresh = createAssetUrl(cachedImage, entry.mimeType);
      imageUrl = fresh;
      ai.updateHistoryEntryUrl(entry.id, fresh);
    }
    applyBatchGenerations([{ shotId, enhancedDataUrl: imageUrl, historyId: effectiveId }]);
  }
}

/** Re-apply a TTS/audio history entry as a new audio clip at the current playhead */
async function reapplyAudioHistoryEntry(entry: GenerationHistoryEntry): Promise<void> {
  try {
    const currentTime = usePlaybackStore.getState().currentTime;
    const { project } = useProjectStore.getState();
    const clipId = useProjectStore.getState()._allocId();
    const { decodeAudio, generatePeaks } = await import('@/audio/audioProcessor.ts');
    const { getCachedAssetBytes } = await import('@/previs/assetStorage.ts');

    // Try byte cache first (survives blob URL revocation), fall back to fetch
    let rawData: Uint8Array;
    const cached = getCachedAssetBytes(entry.objectUrl);
    if (cached) {
      rawData = cached;
    } else {
      const resp = await fetch(entry.objectUrl);
      const buf = await resp.arrayBuffer();
      rawData = new Uint8Array(buf);
    }
    const audioBuffer = await decodeAudio(rawData);
    const peaks = generatePeaks(audioBuffer);

    // Stash raw bytes for save-back (stash-first guarantee)
    stashBlob(clipId, rawData);

    const meta = entry.metadata as Record<string, string> | null;
    const character = meta?.character;
    const label = character
      ? `${character}: ${entry.prompt.slice(0, 30)}${entry.prompt.length > 30 ? '...' : ''}`
      : entry.prompt.slice(0, 40) || 'Speech';

    const { shotId, shotOffset } = project
      ? resolveClipPlacement(currentTime, project)
      : { shotId: 0, shotOffset: currentTime };

    useAudioStore.getState().addAudioClip({
      id: clipId,
      label,
      shotId,
      shotOffset,
      duration: audioBuffer.duration,
      volume: 0.85,
      bufferOffset: 0,
      audioBuffer,
      mimeType: entry.mimeType,
      lane: 0,
      isMuted: false,
      fadeInTime: 0,
      fadeOutTime: 0,
      color: '#4a90d9',
      peaks,
    });

  } catch (err) {
    console.error('Failed to reapply audio history entry:', err);
    import('./uiStore.ts').then(({ useUiStore }) => {
      useUiStore.getState().showAiError(
        i18next.t('errors:audioUnavailableTitle'),
        i18next.t('errors:audioUnavailableMessage'),
      );
    });
  }
}

/**
 * Attach a user-dropped image file to a shot as a static image.
 * Directly sets thumbnailDataUrl — no history entry (not an AI generation) — and clears an applied
 * video, the same reciprocity every other image-apply door honours.
 * Resolves true only when the write actually landed (callers gate their success
 * toast on it — a readOnly flip between drop and confirm must not claim success);
 * rejects when the file cannot be read.
 */
export async function attachDroppedImage(shotId: number, file: File): Promise<boolean> {
  if (isReadOnly()) return false;
  const ps = useProjectStore.getState();
  const { project } = ps;
  if (!project) return false;

  useUndoStore.getState().pushSnapshot();

  // Read file as data URL
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  // Bound the interim tile to 640px — it survives in the store until Unity's thumbnailChanged
  // push replaces it, and an unbounded full-res string here gets pinned into undo snapshots.
  // The full-res dataUrl below still goes to Unity untouched. This await must land BEFORE the
  // flush-then-re-read below, not after — see that comment for why the re-read has to be last.
  const tileUrl = await downscaleDataUrl(dataUrl, 640, 0.8);

  // Drain any buffered push before the write — the awaits above mean one could have arrived
  // since this call started. See sceneReadyBuffer.ts (ORDERING, outbound). The flush has to
  // stay here rather than at the head, so the project is RE-READ after it: `project` above predates
  // both the flush and anything else that landed during the awaits, and writing from it would revert
  // the rest of the flushed batch.
  flushSceneReadyBuffer();
  // videoShots is re-read HERE too, not from the `ps` destructure at the head: that one predates the
  // flush and both awaits above, and the clear below writes the whole array back.
  const { project: current, videoShots } = useProjectStore.getState();
  if (!current) return false;

  // Same reciprocity contract as applyBatchGenerations/acceptEnhancement — an applied image replaces
  // an applied video. Left attached, the video outranks the still just dropped in every display
  // precedence chain (ShotTypeBadge, Viewer, useShotFullImage), so the shot shows one medium and
  // plays the other.
  const updatedScenes = current.scenes.map(scene => ({
    ...scene,
    shots: scene.shots.map(shot =>
      shot.id === shotId
        ? { ...shot, thumbnailDataUrl: tileUrl, historyId: undefined, hasStaticImage: true, videoObjectUrl: undefined }
        : shot
    ),
  }));

  // Same eviction as acceptEnhancement above — dropping a replacement onto a shot that already has
  // an image is the other half of that flow (it is what ShotImageDropConfirm's hasExistingMedia
  // warning exists for).
  dropShotFullImageForPendingWrite(shotId);

  useProjectStore.setState({
    videoShots: videoShots.filter(v => v.shotId !== shotId),
    project: { ...current, scenes: updatedScenes },
  });

  // Persist to Unity
  if (capabilities.bridge) {
    import('@/bridge/embeddedBridge.ts').then(({ sendStaticImageToUnity }) => {
      sendStaticImageToUnity(shotId, dataUrl);
    });
    // 20260825 - And clear the shot's generation FK, the way Revert to Live does. The store write
    // above drops historyId locally, but nothing on the static-image ingest path clears
    // `shots.history_id` in Unity's DB — so an image dropped onto a previously generated shot left
    // the row pointing at that generation forever, and every subsequent load resurrected its (raw,
    // uncropped) pixels onto the tile. Unity's handler writes '' for a null/empty id
    // (TimelineWebViewPanel.HandleUpdateShotHistoryId), which is the same value revertShotToLive
    // writes, so null is the correct wire shape here.
    //
    // UNCONDITIONAL, whatever the FK named, and 'image' is the medium the shot NOW shows. That pair
    // is what makes the drop door behave like every other image-apply door: Unity's handler reads
    // the PRIOR asset type and, on 'image' over a video, deletes the cached {shotId}.mp4 and its
    // .hash sidecar — otherwise Tier 2 of SerializeShotUpdatesForSceneId re-advertises that file on
    // bare File.Exists and the video re-attaches on every repush and reload. Native imports get the
    // same treatment through the twin, TimelineWebViewPanel.ClearAppliedImageGenerationForReplacedShotImage.
    // A missing/dangling FK sends the identical shape and costs nothing (no mp4 to delete).
    import('@/bridge/generationHistorySyncBridge.ts').then(({ sendShotHistoryIdToUnity }) => {
      sendShotHistoryIdToUnity(shotId, null, 'image');
    });
  }
  return true;
}

/**
 * Capture the current frame from the Viewer's video element and set it as the
 * shot's thumbnail. Uses the already-loaded <video> in the DOM so the frame
 * matches exactly what the user sees at the current timecode.
 */
export function setVideoKeyframe(shotId: number): void {
  if (isReadOnly()) return;
  // Drain first, or a buffered push replaces the frame the user just captured.
  flushSceneReadyBuffer();
  const ps = useProjectStore.getState();
  const { project } = ps;
  if (!project) return;

  // Find the Viewer's canvas element (video frames are painted here by useVideoSync)
  const canvas = document.querySelector('canvas[class*="videoCanvas"]') as HTMLCanvasElement | null;
  if (!canvas || canvas.width === 0 || canvas.height === 0) return;
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

  useUndoStore.getState().pushSnapshot();

  const updatedScenes = project.scenes.map(scene => ({
    ...scene,
    shots: scene.shots.map(shot =>
      shot.id === shotId ? { ...shot, thumbnailDataUrl: dataUrl } : shot
    ),
  }));
  // The keyframe is sent to Unity as the shot's static image below, so the old _full extraction is
  // stale from this moment — same eviction as acceptEnhancement above. (No display effect today:
  // the shot has a video, so useShotFullImage is ineligible for it. It matters once the video is
  // removed and the still becomes the display source again.)
  dropShotFullImageForPendingWrite(shotId);
  useProjectStore.setState({ project: { ...project, scenes: updatedScenes } });

  if (capabilities.bridge) {
    import('@/bridge/embeddedBridge.ts').then(({ sendStaticImageToUnity }) => {
      sendStaticImageToUnity(shotId, dataUrl);
    });
  }
}

/**
 * Start transition video generation.
 */
export function startTransitionVideoGen(fromShotId: number, toShotId: number): void {
  const ps = useProjectStore.getState();
  if (isReadOnly()) return;
  const ai = useAiStore.getState();
  if (ai.transitionVideoGenerating) return;

  const allShotsArr = ps.allShots();
  const fShot = allShotsArr.find(sh => sh.id === fromShotId);
  const tShot = allShotsArr.find(sh => sh.id === toShotId);
  if (!fShot || !tShot) return;

  const transition = ps.getTransition(fromShotId, toShotId);
  if (!transition || transition.type !== 'continuous') return;

  const sceneInfo = ps.getSceneForShot(fromShotId);
  if (!sceneInfo) return;

  useAiStore.setState({ transitionVideoGenerating: { fromShotId, toShotId } });

  (async () => {
    try {
      const { generateTransitionVideo } = await import('@/ai/transitionVideoOrchestrator.ts');
      const result = await generateTransitionVideo(
        transition, fShot, tShot, sceneInfo,
        undefined, undefined,
        { modelId: ai.aiSelectedVideoModel },
      );
      useProjectStore.getState().attachTransitionVideo(fromShotId, toShotId, result.videoUrl, 'video/mp4', result.historyId);
      if (useAiStore.getState().retimeToMatchVideo) {
        await retimeTransitionToVideo(fromShotId, toShotId, result.videoUrl);
      }
    } catch (err) {
      console.error('Transition video generation failed:', err);
      import('@/components/Toast.tsx').then(({ showToast }) =>
        showToast(i18next.t('errors:transitionVideoFailed', { message: err instanceof Error ? err.message : 'Unknown error' }), 'error'));
    } finally {
      useAiStore.setState({ transitionVideoGenerating: null });
    }
  })();
}

/**
 * Create a new shot from coverage selection.
 * In embedded mode: round-trips to Unity for a real shot ID.
 * In standalone mode: allocates ID locally and creates a minimal TimelineShot.
 */
export async function createShotFromCoverage(
  sceneId: number,
  elementIds: Array<{ type: 'dialogue' | 'action'; id: number }>,
  coverageColor: string,
): Promise<number | null> {
  if (isReadOnly()) return null;
  const ps = useProjectStore.getState();
  const { project } = ps;
  if (!project) return null;

  useUndoStore.getState().pushSnapshot();

  const scene = project.scenes.find(s => s.id === sceneId);
  if (!scene) return null;

  let shotId: number;
  let shotNumber: string;

  if (capabilities.bridge) {
    // Embedded: ask Unity to create the shot record
    try {
      const { requestShotCreation } = await import('@/bridge/coverageSyncBridge.ts');
      const result = await requestShotCreation(sceneId);
      shotId = result.shotId;
      shotNumber = result.shotNumber;
    } catch (err) {
      console.error('[createShotFromCoverage] Bridge shot creation failed:', err);
      const { showToast } = await import('@/components/Toast.tsx');
      showToast(i18next.t('errors:failedToCreateShot'), 'error');
      return null;
    }
  } else {
    // Standalone: allocate locally
    shotId = ps._allocId();
    const nextOrder = scene.shots.length + 1;
    shotNumber = `${nextOrder}`;
  }

  // Create the TimelineShot and add it to the scene
  const newShot = {
    id: shotId,
    sceneId,
    number: shotNumber,
    name: '',
    order: scene.shots.length + 1,
    duration: 2.5,
    // The lens Unity's CreateShot bakes into the new shot's camera, so the optimistic row and the
    // one the repush brings back agree. 20260922: this was 10 — correct only while the loader read
    // the dead `cameraobjects.focallength` column; now that it derives the real lens from
    // `apertureangle`, a 10 here would show "10mm" in the Viewer badge and InfoBar and then jump to
    // 35mm the moment the project repush landed. See previs/cameraLens.ts.
    focalLength: DEFAULT_NEW_SHOT_FOCAL_LENGTH_MM,
    fStop: 5.6,
    coverageColor,
  };

  const updatedScenes = project.scenes.map(s => {
    if (s.id !== sceneId) return s;
    return { ...s, shots: [...s.shots, newShot] };
  });
  useProjectStore.setState({ project: { ...project, scenes: updatedScenes } });

  // Assign the selected elements to this new shot
  ps.assignElementsToShot(sceneId, elementIds, shotId);

  return shotId;
}
